import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageTitle from '../ui/PageElements/PageTitle/PageTitle'
import { Alert, Divider, Empty, Form, Input, InputNumber, Modal, Tooltip } from 'antd'
import style from './style.m.less'
import { BackArrow, Box, CenteredSpin, DropdownSelector2, IconButton, PrimaryButton } from '../ui'
import { DropdownSelector3 } from './components'
import PageContainer from '../ui/PageElements/PageContainer/PageContainer'
import Card from '../ui/Card/Card'
import { useNavigate } from 'react-router-dom'
import useMount from '../hooks/useMount'
import { CloseOutlined, EyeOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { RequestTypes } from '../hooks'
import useRequest from '../hooks/useRequest'
import request from '../../services/request'

interface User {
    company_name: string
    user_id: string
    is_personal_account: boolean
}

interface GreenTool {
    number: string
    period: string
    volume: number
}

interface ObjectEntry {
    name: string
}

export const IssueOwnershipCertificate: FC = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const [form] = Form.useForm()
    const [CurrentUser, setCurrentUser] = useState<User | null>(null)
    const [NewName, setNewName] = useState<string>()
    const [volume, setVolume] = useState<number | null>()
    const [period, setPeriod] = useState<string>()
    const [object, setObject] = useState<string>('')
    const [greenTool, setGreenTool] = useState<GreenTool | undefined>()
    const [greenVolume, setGreenVolume] = useState<number | null>()

    const [objects, setObjects] = useState<ObjectEntry[]>([])
    const [greenTools, setGreenTools] = useState<GreenTool[]>([])

    const [greenToolsList, setGreenToolsList] = useState<GreenTool[]>([])
    const [UsersList, setUsersList] = useState<User[]>([])

    const [previewVisible, setPreviewVisible] = useState(false)
    const [pdfUrl, setPdfUrl] = useState<string | null>(null)

    const [usersError, setUsersError] = useState(false)
    const [disableSend, setDisableSend] = useState(true)
    const [disablePeriodObjects, setDisablePeriodObjects] = useState(false)
    const [disabledGreenTools, setDisabledGreenTools] = useState(false)
    const [pendingPreview, setPendingPreview] = useState<boolean>(false)

    const { fetch: fetchGreenTools } = useRequest({
        link: '/api/extension/v1/extension/generations_attributes/green_tools',
        method: RequestTypes.get,
    })
    const {
        fetch: fetchUsers,
        result: resultUsers,
        isLoading: isLoadingUsers,
    } = useRequest({
        link: '/api/extension/v1/extension/ownership_certificates/skizi_users/',
        method: RequestTypes.get,
    })
    const { fetch: fetchCertificateCreate } = useRequest({
        link: '/api/extension/v1/extension/ownership_certificates/create',
        method: RequestTypes.post,
    })

    useEffect(() => {
        if (!isLoadingUsers && resultUsers) {
            if (!resultUsers.detail.length) {
                setUsersError(true)
            } else {
                setUsersList(resultUsers.detail)
                setUsersError(false)
            }
        }
    }, [isLoadingUsers, resultUsers])

    useEffect(() => {
        const isReady =
            CurrentUser !== null &&
            !!volume &&
            volume > 0 &&
            objects.length > 0 &&
            greenTools.length > 0

        setDisableSend(!isReady)
    }, [CurrentUser, volume, objects, greenTools])

    useMount(() => {
        getData()
    })

    useEffect(() => {
        if (CurrentUser?.user_id) {
            setGreenTool(undefined)
            setGreenTools([])
            if (CurrentUser.is_personal_account) {
                setObjects([{ name: 'В целях энергоснабжения вышеуказанного помещения' }])
                setDisablePeriodObjects(true)
            } else {
                setObjects([])
                setDisablePeriodObjects(false)
            }
            form.setFieldsValue({
                green_tool: undefined,
                green_volume: undefined,
            })

            getGreenTools(CurrentUser.user_id).catch((e: any) => console.log(e))
        }
    }, [CurrentUser])

    useEffect(() => {
        return () => {
            if (pdfUrl) {
                URL.revokeObjectURL(pdfUrl)
            }
        }
    }, [pdfUrl])

    useEffect(() => {
        setDisabledGreenTools(false)
    }, [greenTool])

    const getData = async () => {
        fetchUsers({
            onError: () => {
                setUsersError(true)
            },
        })
    }

    const getGreenTools = async (user_id: string) => {
        fetchGreenTools({
            addToLink: `?user_id=${user_id}`,
            onSuccess: (data) => {
                setGreenToolsList(
                    data.detail.map((tool: any) => ({
                        number: tool.number,
                        period: tool.period,
                        volume: tool.limit,
                    })),
                )
            },
        })
    }

    const addGenObject = () => {
        if (object.trim()) {
            setObjects((prev) => [...prev, { name: object }])
            setObject('')
            form.setFieldsValue({ object: '' })
        }
    }

    const addGreenTool = () => {
        if (greenTool && greenVolume && greenVolume > 0) {
            const curGreenTool = greenTools.find(
                (item) => item.number === greenTool.number && item.period === greenTool.period,
            )
            const curListGreenTool = greenToolsList.find(
                (item) => item.number === greenTool.number && item.period === greenTool.period,
            )
            if (!curListGreenTool || (curListGreenTool && greenVolume > curListGreenTool?.volume)) {
                return
            }
            if (curListGreenTool) {
                setGreenToolsList((prev) => {
                    prev[greenToolsList.indexOf(curListGreenTool)].volume -= greenVolume
                    return prev
                })
            }
            if (!curGreenTool) {
                setGreenTools((prev) => [
                    ...prev,
                    {
                        number: greenTool.number,
                        volume: greenVolume,
                        period: greenTool.period,
                    },
                ])
            } else {
                setGreenTools((prev) => {
                    prev[greenTools.indexOf(curGreenTool)].volume += greenVolume
                    return prev
                })
            }
            setGreenTool(undefined)
            setGreenVolume(undefined)
            form.setFieldsValue({ green_tool: undefined, green_volume: undefined })
        }
    }

    const onFinish = async () => {
        const totalGreenVolume = greenTools.reduce((sum, tool) => sum + tool.volume, 0)
        setDisableSend(true)
        if (volume !== totalGreenVolume) {
            form.scrollToField('volume')
            return form.setFields([
                {
                    name: 'greenVolume',
                    errors: [t('ownershipCertificate.certForm.greenToolsSumError')],
                },
                {
                    name: 'volume',
                    errors: [t('ownershipCertificate.certForm.certificateVolumeError')],
                },
            ])
        }

        try {
            const payload = {
                user_id: CurrentUser?.user_id,
                new_name: NewName,
                volume: volume,
                period: period,
                objects: objects,
                green_tools: greenTools,
            }
            fetchCertificateCreate({
                body: payload,
                onSuccess: () => navigate('/ownership-certificates'),
                onError: (err) => console.error('Ошибка при создании сертификата:', err),
            })
        } catch (error) {
            console.error('Ошибка при создании сертификата:', error)
            setDisableSend(false)
        }
    }

    const formatDate = (isoDate: string) => {
        const [year, month, day] = isoDate.split('-')
        return `${day}.${month}.${year}`
    }

    const handlePreview = async () => {
        setDisableSend(true)
        setPendingPreview(true)
        try {
            const payload = {
                user_id: CurrentUser?.user_id,
                new_name: NewName,
                volume: volume,
                period: period,
                objects: objects,
                green_tools: greenTools,
            }

            const response = await request.post(
                '/api/extension/v1/files/certificates/ownership/preview',
                payload,
                {
                    responseType: 'blob',
                },
            )

            const blob = new Blob([response.data], { type: 'application/pdf' })
            const objectUrl = window.URL.createObjectURL(blob)

            setPdfUrl(objectUrl)
            setPreviewVisible(true)
        } catch (error) {
            console.error('Ошибка при получении PDF:', error)
        } finally {
            setDisableSend(false)
            setPendingPreview(false)
        }
    }

    if (isLoadingUsers) {
        return <CenteredSpin />
    }
    return (
        <PageContainer className={style.container}>
            <PageTitle
                title={
                    <>
                        <BackArrow link="/ownership-certificates" />
                        {t('greenTools.createOwnershipCertificates')}
                    </>
                }
            />
            {!usersError ? (
                <Card>
                    <Form
                        form={form}
                        onFinish={onFinish}
                        name="issueOwnershipCertificate"
                        layout="vertical"
                    >
                        <Form.Item
                            name="user_id"
                            className={style.dropdownWrapper}
                            label={
                                <span>
                                    {t('ownershipCertificate.certForm.userLabel')}
                                    <Tooltip
                                        title={t('ownershipCertificate.certForm.tooltips.user')}
                                        overlayStyle={{ maxWidth: 300 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </span>
                            }
                            rules={[
                                {
                                    required: true,
                                    message: t('ownershipCertificate.certForm.userRequired'),
                                },
                            ]}
                        >
                            <DropdownSelector2
                                placeholder={t('ownershipCertificate.certForm.userPlaceholder')}
                                customStyle={{ width: '400px' }}
                                options={UsersList?.map((item) => ({
                                    text: item.company_name,
                                    value: item.user_id,
                                    id: item.user_id,
                                }))}
                                onChange={(value) =>
                                    setCurrentUser(
                                        UsersList.find((item) => item.user_id === value) || null,
                                    )
                                }
                                noDefaultValue={true}
                            />
                        </Form.Item>

                        <Form.Item
                            name="new_name"
                            label={
                                <span>
                                    {t('ownershipCertificate.certForm.newNameLabel')}
                                    <Tooltip
                                        title={t('ownershipCertificate.certForm.tooltips.newName')}
                                        overlayStyle={{ maxWidth: 500 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </span>
                            }
                            className={style.dropdownWrapper}
                        >
                            <Input
                                placeholder={t('ownershipCertificate.certForm.newNamePlaceholder')}
                                className={style.input}
                                value={NewName}
                                onChange={(e) => setNewName(e.target.value)}
                            />
                        </Form.Item>
                        <Divider />
                        <Form.Item
                            name="volume"
                            label={
                                <span>
                                    {t('ownershipCertificate.certForm.volumeLabel')}
                                    <Tooltip
                                        title={t('ownershipCertificate.certForm.tooltips.volume')}
                                        overlayStyle={{ maxWidth: 400 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </span>
                            }
                            className={style.dropdownWrapperMargin}
                            rules={[
                                {
                                    required: true,
                                    message: t('ownershipCertificate.certForm.volumeRequired'),
                                },
                            ]}
                        >
                            <InputNumber
                                placeholder={t('ownershipCertificate.certForm.volumePlaceholder')}
                                className={style.input}
                                value={volume}
                                onChange={(value) => setVolume(value)}
                                formatter={(value) =>
                                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                                }
                                controls={false}
                            />
                        </Form.Item>

                        <Form.Item
                            name="period"
                            label={
                                <span>
                                    {t('ownershipCertificate.certForm.periodLabel')}
                                    <Tooltip
                                        title={t('ownershipCertificate.certForm.tooltips.period')}
                                        overlayStyle={{ maxWidth: 300 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </span>
                            }
                            className={style.dropdownWrapper}
                        >
                            <Input
                                placeholder={t('ownershipCertificate.certForm.periodPlaceholder')}
                                className={style.input}
                                value={period}
                                onChange={(e) => setPeriod(e.target.value)}
                                disabled={disablePeriodObjects}
                            />
                        </Form.Item>

                        <Form.Item
                            name="object"
                            label={
                                <span>
                                    {t('ownershipCertificate.certForm.objectsLabel')}
                                    <Tooltip
                                        title={t('ownershipCertificate.certForm.tooltips.objects')}
                                        overlayStyle={{ maxWidth: 700 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </span>
                            }
                            className={style.dropdownWrapper}
                        >
                            <Input
                                placeholder={t('ownershipCertificate.certForm.objectPlaceholder')}
                                className={style.input}
                                value={object}
                                onChange={(e) => setObject(e.target.value)}
                                disabled={disablePeriodObjects}
                            />
                        </Form.Item>

                        <button
                            className={style.primaryButton}
                            type="button"
                            onClick={addGenObject}
                            style={{ padding: '8px 16px' }}
                            disabled={disablePeriodObjects}
                        >
                            {t('ownershipCertificate.certForm.addObjectButton')}
                        </button>

                        <div className={style.agentsGrid}>
                            {objects.map((obj, index) => (
                                <Alert
                                    key={index}
                                    className={style.agentAlert}
                                    message={
                                        <div className={style.alertTitle}>{`${t(
                                            'ownershipCertificate.certForm.objectText',
                                        )}: ${obj.name}`}</div>
                                    }
                                    description={
                                        <CloseOutlined
                                            className={
                                                !disablePeriodObjects
                                                    ? style.alertCloseIcon
                                                    : style.alertCloseIconDisabled
                                            }
                                            onClick={() => {
                                                if (!disablePeriodObjects) {
                                                    setObjects((prev) =>
                                                        prev.filter((_, i) => i !== index),
                                                    )
                                                }
                                            }}
                                            disabled={disablePeriodObjects}
                                        />
                                    }
                                    type="info"
                                />
                            ))}
                        </div>
                        <Divider />
                        <Form.Item
                            name="green_tool"
                            label={
                                <span>
                                    {t('ownershipCertificate.certForm.greenToolsLabel')}
                                    <Tooltip
                                        title={t(
                                            'ownershipCertificate.certForm.tooltips.greenTools',
                                        )}
                                        overlayStyle={{ maxWidth: 600 }}
                                    >
                                        <QuestionCircleOutlined
                                            style={{
                                                marginLeft: 8,
                                                color: 'rgba(0, 0, 0, 0.45)',
                                            }}
                                        />
                                    </Tooltip>
                                </span>
                            }
                            className={style.dropdownWrapper}
                        >
                            <DropdownSelector3
                                placeholder={t(
                                    'ownershipCertificate.certForm.greenToolPlaceholder',
                                )}
                                size={'large'}
                                options={greenToolsList.map((item) => ({
                                    text: `${item.number} ${formatDate(item.period)} (${
                                        item.volume
                                    })`,
                                    value: `${item.number}__${item.period}`,
                                    id: `${item.number}__${item.period}`,
                                }))}
                                onChange={(value) => {
                                    const [number, period] = value.split('__')
                                    const found = greenToolsList.find(
                                        (item) => item.number === number && item.period === period,
                                    )
                                    setGreenVolume(undefined)
                                    form.setFieldsValue({ green_volume: undefined })
                                    setGreenTool(found)
                                }}
                                value={
                                    greenTool
                                        ? `${greenTool.number}__${greenTool.period}`
                                        : undefined
                                }
                            />
                        </Form.Item>

                        <Form.Item
                            name="green_volume"
                            className={style.dropdownWrapperSmMargin}
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value) {
                                            return Promise.resolve()
                                        }

                                        if (!greenTool) {
                                            return Promise.reject(
                                                new Error(
                                                    t(
                                                        'ownershipCertificate.certForm.greenToolRequired',
                                                    ),
                                                ),
                                            )
                                        }

                                        if (value > greenTool.volume) {
                                            setDisabledGreenTools(true)
                                            return Promise.reject(
                                                new Error(
                                                    t(
                                                        'ownershipCertificate.certForm.greenVolumeExceeded',
                                                        {
                                                            max: greenTool.volume,
                                                        },
                                                    ),
                                                ),
                                            )
                                        }
                                        setDisabledGreenTools(false)
                                        return Promise.resolve()
                                    },
                                },
                            ]}
                        >
                            <InputNumber
                                placeholder={t(
                                    'ownershipCertificate.certForm.greenVolumePlaceholder',
                                )}
                                className={style.input}
                                value={greenVolume}
                                onChange={(value) => setGreenVolume(value)}
                                formatter={(value) =>
                                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                                }
                                controls={false}
                            />
                        </Form.Item>

                        <button
                            className={style.primaryButton}
                            type="button"
                            disabled={disabledGreenTools}
                            onClick={addGreenTool}
                            style={{ padding: '8px 16px' }}
                        >
                            {t('ownershipCertificate.certForm.addGreenToolButton')}
                        </button>

                        <div className={style.agentsGrid}>
                            {greenTools.map((tool, index) => (
                                <Alert
                                    key={index}
                                    className={style.agentAlert}
                                    message={
                                        <div className={style.alertTitle}>{`${t(
                                            'ownershipCertificate.certForm.volumeText',
                                        )}: ${tool.volume} ${t(
                                            'ownershipCertificate.certForm.mwh',
                                        )}`}</div>
                                    }
                                    description={
                                        <>
                                            <CloseOutlined
                                                className={style.alertCloseIcon}
                                                onClick={() => {
                                                    setGreenTools((prev) =>
                                                        prev.filter((_, i) => i !== index),
                                                    )
                                                    const curListGreenTool = greenToolsList.find(
                                                        (item) =>
                                                            item.number === tool.number &&
                                                            item.period === tool.period,
                                                    )
                                                    if (curListGreenTool) {
                                                        setGreenToolsList((prev) => {
                                                            prev[
                                                                greenToolsList.indexOf(
                                                                    curListGreenTool,
                                                                )
                                                            ].volume += tool.volume
                                                            return prev
                                                        })
                                                    }
                                                }}
                                            />
                                            <div className={style.alertDetail}>
                                                <span>{`${t(
                                                    'ownershipCertificate.certForm.greenToolNumber',
                                                )}: ${tool.number}`}</span>
                                                <span>{`${t(
                                                    'ownershipCertificate.certForm.periodText',
                                                )}: ${formatDate(tool.period)}`}</span>
                                            </div>
                                        </>
                                    }
                                    type="info"
                                />
                            ))}
                        </div>
                    </Form>
                    <Alert
                        className={style.modalAlert}
                        message={
                            <div className={style.modalAlertText}>
                                <div>{t('ownershipCertificate.certForm.tooltips.submitFirst')}</div>
                                <div>
                                    {t('ownershipCertificate.certForm.tooltips.submitSecond')}
                                </div>
                            </div>
                        }
                        type="warning"
                        showIcon
                    />
                    <Divider />
                    <Box direction="row" justify="flex-start" margin="32px 0px 0px 0px">
                        <PrimaryButton
                            onClick={() => navigate('/ownership-certificates')}
                            value={t('ownershipCertificate.certForm.cancelButton')}
                            isCancel
                            customStyle={{ padding: '8px 16px' }}
                        />
                        <PrimaryButton
                            onClick={() => form.submit()}
                            disabled={disableSend}
                            value={t('ownershipCertificate.certForm.saveButton')}
                            customStyle={{ marginLeft: '16px', padding: '8px 16px' }}
                        />
                        <IconButton
                            icon={<EyeOutlined />}
                            disabled={disableSend}
                            onClick={handlePreview}
                            customIconStyle={{
                                marginRight: '5px',
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                            }}
                            customStyle={{
                                marginLeft: '16px',
                                padding: '8px 16px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: '38px',
                            }}
                            loading={pendingPreview}
                        >
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    height: '100%',
                                    lineHeight: '1.2',
                                }}
                            >
                                Предпросмотр
                            </span>
                        </IconButton>
                    </Box>
                    <Modal
                        visible={previewVisible}
                        onCancel={() => {
                            setPreviewVisible(false)
                            setPdfUrl(null)
                        }}
                        footer={null}
                        width={900}
                        bodyStyle={{
                            width: 900,
                            height: 900,
                            borderRadius: 12,
                            overflow: 'hidden',
                            padding: 0,
                        }}
                        destroyOnClose
                        centered
                        closable={false}
                    >
                        {pdfUrl && (
                            <iframe
                                src={pdfUrl ?? ''}
                                title="Предпросмотр"
                                width="900px"
                                height="900px"
                                style={{ border: 'none' }}
                            />
                        )}
                    </Modal>
                </Card>
            ) : (
                <Card className={style.emptyCard}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('ownershipCertificate.certForm.emptyText')}
                    />
                </Card>
            )}
        </PageContainer>
    )
}

export default IssueOwnershipCertificate
